import { useLayoutEffect, useRef } from 'react';

// Session-only scroll offsets keyed by an arbitrary id (e.g. a route path).
const positions = new Map<string, number>();

/** Clears all saved offsets — call on sign-out / account switch. */
export function clearScrollPositions() {
  positions.clear();
}

const VIEWPORT_SELECTOR = '[data-radix-scroll-area-viewport], .rt-ScrollAreaViewport';

// Keys that scroll a focused container. Other keys (Tab, Enter…) navigate or
// activate without scrolling and must not cancel restoration.
const SCROLL_KEYS = new Set([
  ' ',
  'PageUp',
  'PageDown',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
]);

/**
 * Saves and restores the scroll position of a scrollable element (or a Radix
 * Themes `ScrollArea`, whose inner viewport is the scroller) across unmounts.
 *
 * Attach the returned ref to the scroll container. The saved offset is
 * re-applied on mount and whenever the content resizes — the grid can briefly
 * collapse or grow after mount while data settles — until the user scrolls.
 */
export function useScrollRestoration<T extends HTMLElement>(key: string) {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    // Radix Themes forwards the ScrollArea ref to the inner viewport (the
    // actual scroller); a plain container ref needs a descendant lookup.
    const viewport = root.matches(VIEWPORT_SELECTOR)
      ? root
      : (root.querySelector<HTMLElement>(VIEWPORT_SELECTOR) ?? root);

    const onScroll = () => positions.set(key, viewport.scrollTop);
    viewport.addEventListener('scroll', onScroll, { passive: true });

    let observer: ResizeObserver | undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) stopRestoring();
    };
    const stopRestoring = () => {
      observer?.disconnect();
      observer = undefined;
      viewport.removeEventListener('wheel', stopRestoring);
      viewport.removeEventListener('touchstart', stopRestoring);
      viewport.removeEventListener('pointerdown', stopRestoring);
      viewport.removeEventListener('keydown', onKeyDown);
    };

    const saved = positions.get(key);
    if (saved) {
      const restore = () => {
        if (viewport.scrollTop !== saved) viewport.scrollTop = saved;
      };
      restore();
      // The viewport's own size is fixed; watch the content inside it, which
      // changes as profiles render, refetch, or lazy images load.
      observer = new ResizeObserver(restore);
      observer.observe(viewport.firstElementChild ?? viewport);
      viewport.addEventListener('wheel', stopRestoring, { passive: true });
      viewport.addEventListener('touchstart', stopRestoring, { passive: true });
      viewport.addEventListener('pointerdown', stopRestoring, { passive: true });
      // keydown bubbles up from focused descendants, covering keyboard
      // scrolling whether the viewport itself or a card inside it has focus.
      viewport.addEventListener('keydown', onKeyDown);
    }

    return () => {
      positions.set(key, viewport.scrollTop);
      viewport.removeEventListener('scroll', onScroll);
      stopRestoring();
    };
  }, [key]);

  return ref;
}
